// The sheet's page viewer (PR-E): the page image(s) the model read, from the uploader-
// scoped page route (GET /api/sms/chat/roster/:id/pages/:n — never cached). When the read
// placed the line on its page (O180/O181: `rowPage` + `rowBand`, per-mille of this very
// image's height), the viewer opens on that page with the row highlighted — a full-width
// band — and scrolled to the middle of the dialog; otherwise the whole page is shown with
// the line named above it, as before.
import React, { useRef, useState } from 'react';
import { SheetModal } from './sheet-modal.jsx';
import { sheetPageUrl, centredScrollTop, ROW_BAND_SCALE } from '../../lib/refill-sheet.js';

// A 0–1 fraction as a CSS percentage, without float noise in the markup.
const pct = (v) => `${Number((v * 100).toFixed(4))}%`;

// The dialog's own scroll container (.modal is `overflow-y: auto`): the highlight is
// brought to its middle there, and nothing outside the dialog moves.
function scrollToHighlight(el) {
  if (!el || typeof el.closest !== 'function') return;
  const box = el.closest('.modal');
  if (!box) return;
  const b = box.getBoundingClientRect();
  const r = el.getBoundingClientRect();
  box.scrollTop = centredScrollTop({
    targetTop: r.top - b.top + box.scrollTop,
    targetHeight: r.height,
    viewportHeight: box.clientHeight,
    scrollHeight: box.scrollHeight,
  });
}

export function SheetViewer({ rosterId, pageCount = 1, line = null, page: startPage = 1, highlight = null, facilityName = null, onClose }) {
  const pages = Math.max(1, Number(pageCount) || 1);
  const first = Math.min(pages, Math.max(1, Number(startPage) || 1));
  const [page, setPage] = useState(first);
  const [failed, setFailed] = useState({});
  const closeRef = useRef(null);
  const markRef = useRef(null);
  const title = line != null ? `Sheet — line ${line}` : 'Sheet';
  const marked = !!highlight && page === first;
  return (
    <SheetModal
      title={title}
      subtitle={facilityName || undefined}
      onClose={onClose}
      className="rs-viewer"
      labelId="rs-viewer-title"
      initialFocus={closeRef}
      footer={(
        <>
          {pages > 1 && (
            <div className="rs-viewer-pages" role="group" aria-label="Pages">
              <button type="button" className="btn ghost sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))}>
                Previous page
              </button>
              <span aria-live="polite">Page {page} of {pages}</span>
              <button type="button" className="btn ghost sm" disabled={page >= pages} onClick={() => setPage((p) => Math.min(pages, p + 1))}>
                Next page
              </button>
            </div>
          )}
          <button ref={closeRef} type="button" className="btn primary" onClick={onClose}>Close</button>
        </>
      )}
    >
      {line != null && (
        <div className="rs-viewer-note" data-role="viewer-note">
          {highlight
            ? `Line ${line} is highlighted on page ${first}.`
            : `Line ${line} is on this sheet. Positions aren't recorded, so the whole page is shown.`}
        </div>
      )}
      <div className="rs-viewer-frame">
        {failed[page] ? (
          <div className="rs-error" role="alert">This page couldn't be loaded. The sheet may have expired (sheets are kept 30 days).</div>
        ) : (
          <span className="rs-viewer-page">
            <img
              src={sheetPageUrl(rosterId, page)}
              alt={`Page ${page} of the refill sheet${facilityName ? ` from ${facilityName}` : ''}`}
              onLoad={() => { if (marked) scrollToHighlight(markRef.current); }}
              onError={() => setFailed((f) => ({ ...f, [page]: true }))}
            />
            {marked && (
              <span
                ref={markRef}
                className="rs-viewer-mark"
                data-role="viewer-mark"
                aria-hidden="true"
                style={{
                  left: 0,
                  right: 0,
                  top: pct(highlight.y0 / ROW_BAND_SCALE),
                  height: pct((highlight.y1 - highlight.y0) / ROW_BAND_SCALE),
                }}
              />
            )}
          </span>
        )}
      </div>
    </SheetModal>
  );
}
